/* Filter redesign (2026-07-10): small enhancements for the collection filter
 * UI. Pure progressive enhancement - removing this file leaves a fully working
 * filter (neutral slider track, plain "Apply" label, draggable handles, plain
 * read-only price labels).
 *
 * 1) Price slider fill: keeps the brand-colored segment between the two
 *    range handles in sync by setting --fill-left / --fill-right CSS vars
 *    on the <range-slider> element (consumed by range-slider.css).
 *
 * 2) Apply button result count: appends the already-localized results text
 *    ("39 products" / "39 Produkte" / ...) to the mobile drawer's Apply
 *    button, e.g. "Apply · 39 products". Both parts come from the theme's
 *    own translations, so no new locale keys are needed.
 *
 * 3) (2026-08-24) Click-to-jump on the slider track. range-slider.css now
 *    gives the hit area to the THUMBS only - that is what makes the left
 *    handle reachable at all, see the comment there - but it also means a
 *    press on the bare track reaches no input. This moves whichever handle
 *    is nearer, i.e. what a track press used to do except that it always
 *    grabbed max. Still progressive enhancement: without this file the
 *    handles drag fine, only the track press does nothing.
 *
 * 4) (2026-08-24) The two price boxes accept typing. They are <span>s that
 *    filters.js writes to with innerText, so they are made contenteditable
 *    rather than swapped for <input>: filters.js and both filter snippets
 *    stay untouched. Enter or leaving the box applies, Escape reverts.
 */
(function () {
  'use strict';
  if (window.__hhFiltersExtras) return;
  window.__hhFiltersExtras = true;

  function updateFill() {
    var sliders = document.querySelectorAll('range-slider');
    sliders.forEach(function (rs) {
      var min = rs.querySelector('input.min');
      var max = rs.querySelector('input.max');
      if (!min || !max) return;
      var top = parseFloat(max.max) || 0;
      if (top <= 0) return;
      var lo = Math.max(0, Math.min(parseFloat(min.value) || 0, top));
      var hi = Math.max(lo, Math.min(parseFloat(max.value) || top, top));
      rs.style.setProperty('--fill-left', (lo / top * 100).toFixed(2) + '%');
      rs.style.setProperty('--fill-right', (hi / top * 100).toFixed(2) + '%');
    });
  }

  function updateApply() {
    var apply = document.querySelector('.filters-sidebar_apply');
    var results = document.querySelector('.filter-header_results-text');
    if (!apply) return;
    if (!apply.dataset.baseLabel) apply.dataset.baseLabel = apply.textContent.trim();
    var txt = results ? results.textContent.trim() : '';
    apply.textContent = txt ? apply.dataset.baseLabel + ' · ' + txt : apply.dataset.baseLabel;
  }

  function refresh() {
    updateFill();
    updateApply();
    makeEditable();
  }

  /* live slider dragging */
  document.addEventListener('input', function (e) {
    if (e.target && e.target.closest && e.target.closest('range-slider')) updateFill();
  });

  /* press on the bare track -> move the nearer handle there */
  document.addEventListener('pointerdown', function (e) {
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    var el = e.target;
    if (!el || !el.closest) return;
    /* a press that landed on a thumb is a native drag - leave it alone */
    if (el.tagName === 'INPUT') return;
    /* the value boxes below the track are their own control */
    if (el.closest('.range_wrapper')) return;
    var rs = el.closest('range-slider');
    if (!rs) return;

    var min = rs.querySelector('input.min');
    var max = rs.querySelector('input.max');
    if (!min || !max) return;

    /* only the slider's own band, not the value boxes underneath it */
    var box = min.getBoundingClientRect();
    if (e.clientY < box.top || e.clientY > box.bottom || !box.width) return;

    var top = parseFloat(max.max) || 0;
    if (top <= 0) return;
    var ratio = Math.min(1, Math.max(0, (e.clientX - box.left) / box.width));
    var value = ratio * top;

    var target = Math.abs(value - (parseFloat(min.value) || 0)) <=
      Math.abs(value - (parseFloat(max.value) || top)) ? min : max;
    target.value = value;
    /* bubbles, so both RangeSlider.onSlideWatch (labels, clamping, data-load)
       and the debounced filters-form submit pick it up, exactly as a drag does */
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }, true);

  /* -------------------------------------------------------------------
     4) Typing a price into the two value boxes.

     The boxes are <span class="range_min/max">, not inputs: filters.js
     writes into them with innerText (onSlideWatch / resetRanges) and
     renderPriceUpdates replaces the whole .range_wrapper after every
     fetch. Turning them into real <input> would mean editing filters.js
     and both filter snippets, so instead they are made contenteditable -
     innerText writes keep working untouched, and everything below stays
     inside this file.

     Because the wrapper is replaced on every fetch, all of this is
     delegated from document and the attributes are re-applied by the
     MutationObserver in observe().
     ------------------------------------------------------------------- */
  var CELL = '.range_min, .range_max';

  var editMode = (function () {
    var probe = document.createElement('div');
    try {
      probe.setAttribute('contenteditable', 'plaintext-only');
      if (probe.contentEditable === 'plaintext-only') return 'plaintext-only';
    } catch (err) { /* older browser - fall through */ }
    return 'true';
  })();

  function makeEditable() {
    document.querySelectorAll('range-slider').forEach(function (rs) {
      /* the theme already renders localized visually-hidden labels for the two
         range inputs ("Von"/"Bis", "Od"/"Do") - reuse them, no new locale keys */
      var labels = rs.querySelectorAll('label.visually-hidden');
      rs.querySelectorAll(CELL).forEach(function (cell, i) {
        if (cell.getAttribute('contenteditable') === editMode) return;
        cell.setAttribute('contenteditable', editMode);
        cell.setAttribute('inputmode', 'decimal');
        cell.setAttribute('spellcheck', 'false');
        cell.setAttribute('role', 'textbox');
        if (labels[i]) cell.setAttribute('aria-label', labels[i].textContent.trim());
      });
    });
  }

  /* "26,90" / "1.234,56" / "1,234.56" / "1 234 Kc" / "EUR 26.90" -> a number.
     The LAST separator decides: one with 1-2 digits behind it is the decimal
     point, anything else is a thousands separator. */
  function parseAmount(text) {
    var s = String(text).replace(/[^\d.,]/g, '');
    if (!s) return null;
    var cut = Math.max(s.lastIndexOf(','), s.lastIndexOf('.'));
    if (cut === -1) return parseFloat(s);
    var decimals = s.length - cut - 1;
    if (decimals >= 1 && decimals <= 2) {
      return parseFloat(s.slice(0, cut).replace(/[.,]/g, '') + '.' + s.slice(cut + 1));
    }
    return parseFloat(s.replace(/[.,]/g, ''));
  }

  function formatCell(cell, value) {
    if (!cell) return;
    if (window.Shopify && Shopify.formatMoney && window.money_format) {
      cell.textContent = Shopify.formatMoney(parseFloat(value) * 100, window.money_format);
    }
  }

  /* put both boxes back to what the sliders actually hold (bad input, no change,
     or Escape) */
  function restore(rs) {
    formatCell(rs.querySelector('.range_min'), rs.querySelector('input.min').value);
    formatCell(rs.querySelector('.range_max'), rs.querySelector('input.max').value);
  }

  function commit(cell) {
    var rs = cell.closest('range-slider');
    if (!rs) return;
    var min = rs.querySelector('input.min');
    var max = rs.querySelector('input.max');
    if (!min || !max) return;

    var ceiling = parseFloat(max.max) || 0;
    var isMin = cell.classList.contains('range_min');
    var input = isMin ? min : max;
    var value = parseAmount(cell.textContent);

    if (value === null || isNaN(value)) { restore(rs); return; }

    value = Math.min(Math.max(value, 0), ceiling);
    /* the two ends may not cross - same rule onSlideWatch applies to a drag */
    value = isMin
      ? Math.min(value, parseFloat(max.value))
      : Math.max(value, parseFloat(min.value));

    if (parseFloat(input.value) === value) { restore(rs); return; }

    input.value = value;
    /* bubbles: onSlideWatch reformats both boxes and the debounced
       filters-form submit fetches, exactly as after a drag */
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  /* Typing fires input events that would bubble to the form and kick off a
     fetch on every keystroke - and renderPriceUpdates would then replace the
     box mid-word. Swallow them in the capture phase; the edit itself has
     already happened. */
  document.addEventListener('input', function (e) {
    if (e.target && e.target.closest && e.target.closest(CELL)) e.stopPropagation();
  }, true);

  document.addEventListener('keydown', function (e) {
    var cell = e.target && e.target.closest && e.target.closest(CELL);
    if (!cell) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      cell.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      var rs = cell.closest('range-slider');
      if (rs) restore(rs);
      cell.blur();
    }
  });

  document.addEventListener('focusout', function (e) {
    var cell = e.target && e.target.closest && e.target.closest(CELL);
    if (cell && cell.isConnected) commit(cell);
  });

  /* after every filter fetch the theme re-renders .filters-head (results
     text) and the range values - observe and re-sync */
  function observe() {
    var head = document.querySelector('.filters-head');
    if (head) new MutationObserver(refresh).observe(head, { childList: true, subtree: true });
    var range = document.getElementById('price-range');
    if (range) {
      /* renderPriceUpdates replaces the whole .range_wrapper, so the boxes have
         to be made editable again after every fetch. The filter is scoped so
         setting contenteditable here cannot retrigger this observer. */
      new MutationObserver(function () { updateFill(); makeEditable(); })
        .observe(range, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'data-load'] });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { refresh(); observe(); });
  } else {
    refresh();
    observe();
  }
})();
